import { partition, flatMap, values } from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";

import Alert from "antd/lib/alert";
import Tabs from "antd/lib/tabs";
import * as Grid from "antd/lib/grid";
import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import Layout from "@/components/admin/Layout";
import { CounterCard, WorkersTable, QueuesTable, QueryJobsTable, OtherJobsTable } from "@/components/admin/RQStatus";

import { axios } from "@/services/axios";
import location from "@/services/location";
import recordEvent from "@/services/recordEvent";
import routes from "@/services/routes";
import { useTheme } from "@/components/ThemeProvider";

function Jobs() {
  const [state, setState] = useState({
    activeTab: location.hash,
    isLoading: true,
    error: null,
    queueCounters: [],
    overallCounters: { started: 0, queued: 0 },
    startedJobs: [],
    workers: [],
  });

  const { isDarkMode } = useTheme();

  const processQueues = useCallback(({ queues, workers }) => {
    const queueCounters = values(queues).map(({ started, ...rest }) => ({
      started: started.length,
      ...rest,
    }));

    const overallCounters = queueCounters.reduce(
      (c, q) => ({
        started: c.started + q.started,
        queued: c.queued + q.queued,
      }),
      { started: 0, queued: 0 }
    );

    const startedJobs = flatMap(values(queues), queue =>
      queue.started.map(job => ({
        ...job,
        enqueued_at: moment.utc(job.enqueued_at),
        started_at: moment.utc(job.started_at),
      }))
    );

    setState(prevState => ({ 
      ...prevState,
      isLoading: false, 
      queueCounters, 
      startedJobs, 
      overallCounters, 
      workers 
    }));
  }, []);

  const handleError = useCallback(error => {
    setState(prevState => ({ ...prevState, isLoading: false, error }));
  }, []);

  const refresh = useCallback(() => {
    axios
      .get("/api/admin/queries/rq_status")
      .then(data => processQueues(data))
      .catch(error => handleError(error));
  }, [processQueues, handleError]);

  useEffect(() => {
    recordEvent("view", "page", "admin/rq_status");
    refresh();

    const refreshTimer = setInterval(refresh, 60 * 1000);

    return () => {
      clearInterval(refreshTimer);
    };
  }, [refresh]);

  const changeTab = useCallback(newTab => {
    location.setHash(newTab);
    setState(prevState => ({ ...prevState, activeTab: newTab }));
  }, []);

  const { isLoading, error, queueCounters, startedJobs, overallCounters, workers, activeTab } = state;
  const [startedQueryJobs, otherStartedJobs] = partition(startedJobs, [
    "name",
    "redash.tasks.queries.execution.execute_query",
  ]);

  return (
    <Layout activeTab="jobs">
      <div className={isDarkMode ? "dark-mode" : ""}>
        <div className="p-15">
          {error && <Alert type="error" message="Failed loading status. Please refresh." />}

          {!error && (
            <React.Fragment>
              <Grid.Row gutter={15} className="m-b-15">
                <Grid.Col span={8}>
                  <CounterCard title="Started Jobs" value={overallCounters.started} loading={isLoading} />
                </Grid.Col>
                <Grid.Col span={8}>
                  <CounterCard title="Queued Jobs" value={overallCounters.queued} loading={isLoading} />
                </Grid.Col>
              </Grid.Row>

              <Tabs activeKey={activeTab || "queues"} onTabClick={changeTab} animated={false}>
                <Tabs.TabPane key="queues" tab="Queues">
                  <QueuesTable loading={isLoading} items={queueCounters} />
                </Tabs.TabPane>
                <Tabs.TabPane key="workers" tab="Workers">
                  <WorkersTable loading={isLoading} items={workers} />
                </Tabs.TabPane>
                <Tabs.TabPane key="queries" tab="Queries">
                  <QueryJobsTable loading={isLoading} items={startedQueryJobs} />
                </Tabs.TabPane>
                <Tabs.TabPane key="other" tab="Other Jobs">
                  <OtherJobsTable loading={isLoading} items={otherStartedJobs} />
                </Tabs.TabPane>
              </Tabs>
            </React.Fragment>
          )}
        </div>
      </div>
    </Layout>
  );
}

routes.register(
  "Admin.Jobs",
  routeWithUserSession({
    path: "/admin/queries/jobs",
    title: "RQ Status",
    render: pageProps => <Jobs {...pageProps} />,
  })
);

export default Jobs;